import * as React from 'react';
import { observer } from 'mobx-react';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { GtPointsLayer } from '@openlayers/points/GtPointsLayer';
import { Tooltip } from 'primereact/tooltip';
import { MapLineLabelsDialog } from '@shared/map/MapLineLabelsDialog';
import { StringlinesLayer } from '@openlayers/stringlines/StringlinesLayer';

@observer
export class ModelsPanelToolbar extends React.Component<
    {
        settings: { layerOpacity: number; layerOpacityModels: number; layerOpacityLines: number; layerOpacityDrawings: number };
        onLayerOpacityChange: (opacity: number) => void;
        onLayerOpacityModelsChange: (opacity: number) => void;
        onLayerOpacityLinesChange: (opacity: number) => void;
        onLayerOpacityDrawingsChange: (opacity: number) => void;
        pointsLayer: GtPointsLayer;
        stringlinesLayer: StringlinesLayer;
    },
    {}
> {
    readonly labelPropNames = {
        none: __('general.none'),
        x: 'X',
        y: 'Y',
        z: 'Z',
        xyz: 'XYZ',
        code: __('js_logpoints.column_code'),
        linecode: __('js_logpoints.column_linecode'),
        surfacecode: __('js_logpoints.column_surfacecode'),
        number: __('js_logpoints.column_point_number'),
        prop_name: __('js_logpoints.column_point_name'),
    };

    dropdown: Dropdown = null;

    render() {
        const { layerOpacity, layerOpacityModels, layerOpacityLines, layerOpacityDrawings } = this.props.settings;
        return (
            <>
                <span style={{ marginLeft: 'auto' }}>
                    <Dropdown ref={el => (this.dropdown = el)} closeOnBodyClick={false} buttonStyle={{ paddingLeft: 10, paddingRight: 10 }} className='ik-button-secondary--subtle'>
                        <i className='iki iki-more' />
                        <div className='no-select'>
                            <div className='dropdown-list-title'>{__('general.opacity')}:</div>
                            <div className='flex-row ai-center' style={{ padding: '2px 12px' }}>
                                <span style={{ marginRight: 20 }}>{__('general.model')}</span>
                                <input
                                    type='range'
                                    style={{ width: 100, marginLeft: 'auto' }}
                                    min={20}
                                    max={100}
                                    step={10}
                                    value={layerOpacity}
                                    onChange={e => this.props.onLayerOpacityChange(parseInt(e.currentTarget.value))}
                                />
                                <strong style={{ width: '3em' }}> {layerOpacity}%</strong>
                            </div>
                            <div className='flex-row ai-center' style={{ padding: '2px 12px' }}>
                                <span style={{ paddingLeft: 20, marginRight: 20 }}>{__('js_map.opacity_surfaces')}</span>
                                <input
                                    type='range'
                                    style={{ width: 100, marginLeft: 'auto' }}
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={Math.round((layerOpacityModels * layerOpacity) / 100)}
                                    onChange={e =>
                                        this.props.onLayerOpacityModelsChange(Math.max(20, Math.min(100, Math.round((parseInt(e.currentTarget.value) * 100) / layerOpacity))))
                                    }
                                />
                                <strong style={{ width: '3em' }}> {(layerOpacityModels * layerOpacity) / 100}%</strong>
                            </div>
                            <div className='flex-row ai-center' style={{ padding: '2px 12px' }}>
                                <span style={{ paddingLeft: 20, marginRight: 20 }}>{__('js_map.opacity_lines_points')}</span>
                                <input
                                    type='range'
                                    style={{ width: 100, marginLeft: 'auto' }}
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={Math.round((layerOpacityLines * layerOpacity) / 100)}
                                    onChange={e =>
                                        this.props.onLayerOpacityLinesChange(Math.max(20, Math.min(100, Math.round((parseInt(e.currentTarget.value) * 100) / layerOpacity))))
                                    }
                                />
                                <strong style={{ width: '3em' }}> {(layerOpacityLines * layerOpacity) / 100}%</strong>
                            </div>
                            <div className='flex-row ai-center' style={{ padding: '2px 12px' }}>
                                <span style={{ marginRight: 20 }}>{__('js_map.opacity_drawings')}</span>
                                <input
                                    style={{ width: 100, marginLeft: 'auto' }}
                                    type='range'
                                    min={20}
                                    max={100}
                                    step={10}
                                    value={layerOpacityDrawings}
                                    onChange={e => this.props.onLayerOpacityDrawingsChange(parseInt(e.currentTarget.value))}
                                />
                                <strong style={{ width: '3em' }}> {layerOpacityDrawings}%</strong>
                            </div>

                            <div className='dropdown-list-title flex-row ai-center'>{__('general.settings')}:</div>
                            <ul className='dropdown-list clickable'>
                                <li
                                    onClick={() => {
                                        this.dropdown.close();
                                        MapLineLabelsDialog.open(this.props.stringlinesLayer.lineLabelProps, this.props.stringlinesLayer.setLineLabelProps);
                                    }}
                                >
                                    <span>
                                        <i className='iki iki-text'></i>
                                    </span>
                                    <span>{__('js_map.labels.show_line_labels_on_map')}...</span>
                                </li>
                                <li onClick={() => this.props.stringlinesLayer.setNoNamesInLabels(!this.props.stringlinesLayer.noNamesInLabel)}>
                                    <input type='checkbox' checked={!this.props.stringlinesLayer.noNamesInLabel} readOnly />
                                    {__('js_map.logpoints.show_property_names_in_labels')}
                                </li>
                                <li onClick={this.props.pointsLayer.toggleDeviations}>
                                    <input type='checkbox' checked={this.props.pointsLayer.showDeviations} readOnly />
                                    {__('js_map.labels.show_points_deviation_vectors')}
                                </li>
                            </ul>

                            <Tooltip target='.models_point_label_icon' baseZIndex={99999}>
                                <div>{__('js_map.labels_are_shows_when_zoomed_in')}</div>
                                <div style={{ marginTop: 15 }}>{__('js_map.labels.shift_click_to_select_multiple')}</div>
                            </Tooltip>
                            <div className='dropdown-list-title flex-row ai-center'>
                                {__('js_map.points.point_label')}&nbsp;
                                <i className='iki iki-help models_point_label_icon' />:
                            </div>
                            <ul className='dropdown-list clickable'>
                                <li onClick={this.props.stringlinesLayer.toggleShowPoints}>
                                    <input type='checkbox' checked={this.props.stringlinesLayer.showLinePoints} readOnly />
                                    {__('js_map.labels.show_point_info_for_lines')}
                                </li>
                                {Object.entries(this.labelPropNames).map(([prop, label]) => {
                                    return (
                                        <li key={prop} onClick={e => this.setLabelProp(e, prop)} title={__('js_map.labels.shift_click_to_select_multiple')}>
                                            <input type='radio' checked={this.labelProps.includes(prop)} readOnly />
                                            {label}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </Dropdown>
                </span>
            </>
        );
    }

    get labelProps() {
        if (this.props.pointsLayer.labelProps.length === 0) {
            return ['none'];
        }
        return this.props.pointsLayer.labelProps;
    }

    private setLabelProp = (event, prop: string) => {
        if (prop === 'none') {
            this.props.pointsLayer.setLabelProps([]);
        } else {
            if (event.altKey || event.shiftKey || event.ctrlKey) {
                this.props.pointsLayer.toggleLabelProp(prop);
            } else {
                this.props.pointsLayer.setLabelProps(prop);
            }
        }
    };
}
